import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { addOrder } from "../redux/ordersSlice";
import { useTheme } from "../context/ThemeContext";
import { toast } from "react-hot-toast";
import axios from "axios";
import { ORDER_API, PRODUCT_API, BASE_API_URL } from "../data/api";
import CheckoutPopup from "./CheckoutPopup";

import {
  ArrowLeft,
  ShoppingCart,
  Heart,
  Share2,
  Star,
  Truck,
  Shield,
  Clock,
  ChevronRight,
  Package,
  Zap,
  Lock,
  Sparkles,
  Plus,
  Minus,
  Eye,
  Tag,
  Award,
  BarChart3,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const reduxProducts = useSelector((state) => state.products.list);
  const subscription = useSelector((state) => state.subscription);

  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [activeTab, setActiveTab] = useState("description");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        let prod = reduxProducts.find((p) => p.id.toString() === id);
        if (!prod) {
          const response = await axios.get(PRODUCT_API.GET_ALL);
          const transformed = response.data.map((item) => {
            let imageUrl = "https://via.placeholder.com/300x200?text=No+Image";

            if (item.images?.length > 0) {
              const imgName = item.images[0]?.imageURL;
              if (imgName && imgName !== "string") {
                imageUrl = `${BASE_API_URL.replace("/api", "")}/uploads/${encodeURIComponent(imgName)}`;
              }
            }

            return {
              id: item.productId,
              name: item.productName || "Unnamed Product",
              price: item.price ?? 0,
              imageUrl,
              shortDescription: item.description || "No description available",
              discountPercent: item.discountPercent || 0,
              type: item.productType || "General",
              capacityKG: item.capacityKG || 0,
              speedMPS: item.speedMPS || 0,
              machineRoomType: item.machineRoomType || "N/A",
              doorType: item.doorType || "N/A",
              floorsSupported: item.floorsSupported || 0,
              warrantyYears: item.warrantyYears || 0,
              features: item.features || "No features listed",
              isCustomizable: item.isCustomizable || false,
            };
          });

          prod = transformed.find((p) => p.id.toString() === id);
          if (!prod) {
            toast.error("Product not found");
            navigate("/");
            return;
          }
        }
        setProduct(prod);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load product");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, reduxProducts, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1 }}
          className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }
  if (!product) return null;

  const getFinalPrice = (price, discountPercent = 0) =>
    Math.round(
      price - (price * (subscription.discount || discountPercent || 0)) / 100
    );

  // COD Payment
  const handleBuyCOD = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      toast.error("User not logged in!");
      return;
    }
    const totalAmount = product.price * quantity;
    const discount = subscription.discount || 0;
    const finalAmount = totalAmount - discount;

    try {
      const res = await axios.post(ORDER_API.CREATE, {
        userId: user.userId,
        totalAmount,
        discount,
        finalAmount,
        items: [{ productId: product.id, quantity, price: product.price }],
      });

      if (res.data?.order?.orderId) {
        const order = res.data.order;
        dispatch(
          addOrder({
            id: order.orderId,
            item: product.name,
            amount: `₹${finalAmount.toLocaleString()}`,
            status: order.orderStatus || "Pending",
            qty: quantity,
            paymentMethod: "COD",
            address: user.address || "Default Address",
            placedAt: order.createdAt,
            date: new Date().toLocaleDateString(),
            time: new Date().toLocaleTimeString(),
            thumbnail: product.imageUrl,
          })
        );
        toast.success("Order placed successfully!");
        setShowModal(false);
      } else {
        toast.error("Failed to place order");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to place order");
    }
  };

  // Wallet Payment
  const handleWalletPayment = async () => {
    const finalAmount =
      getFinalPrice(product.price, product.discountPercent) * quantity;
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.userId;
    if (!userId) {
      toast.error("User not logged in!");
      return;
    }

    try {
      const balanceRes = await axios.get(`${BASE_API_URL}/Wallet/${userId}`);
      const currentBalance = balanceRes.data.balance || 0;
      if (currentBalance < finalAmount) {
        toast.error("Insufficient wallet balance!");
        return;
      }

      await axios.post(
        `${BASE_API_URL}/Wallet/spend`,
        {
          userId,
          amount: finalAmount,
          note: `Purchase: ${product.name} x ${quantity}`,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      dispatch(
        addOrder({
          id: `ORD${Math.floor(Math.random() * 10000)}`,
          item: product.name,
          amount: `₹${finalAmount.toLocaleString()}`,
          status: "Processing",
          qty: quantity,
          paymentMethod: "Wallet",
          address: user.address || "Default Address",
          placedAt: new Date().toISOString(),
          date: new Date().toLocaleDateString(),
          time: new Date().toLocaleTimeString(),
          thumbnail: product.imageUrl,
        })
      );

      toast.success("Payment successful! Admin will contact you within 7 days.");
      setShowModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
    }
  };

  const specs = [
    { icon: BarChart3, label: "Type", value: product.type },
    { icon: Package, label: "Capacity", value: `${product.capacityKG} kg` },
    { icon: Zap, label: "Speed", value: `${product.speedMPS} m/s` },
    { icon: Shield, label: "Machine Room", value: product.machineRoomType },
    { icon: Lock, label: "Door Type", value: product.doorType },
    { icon: ChevronRight, label: "Floors Supported", value: product.floorsSupported },
    { icon: Award, label: "Warranty", value: `${product.warrantyYears} yrs` },
    { icon: Sparkles, label: "Customizable", value: product.isCustomizable ? "Yes" : "No" },
  ];

  return (
    <div className={`min-h-screen bg-gray-50 ${theme}`}>
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-6"
          >
            <ArrowLeft size={20} />
            <span>Back to Products</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Eye className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Product Details</h1>
              <p className="text-white/80 text-sm">View and purchase our premium products</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={product.imageUrl}
                alt={product.name}
                className="w-full h-[500px] object-cover"
              />

              {/* Overlay Actions */}
              <div className="absolute top-4 right-4 flex flex-col gap-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsLiked(!isLiked)}
                  className={`w-12 h-12 rounded-full backdrop-blur-lg flex items-center justify-center ${
                    isLiked ? "bg-red-500 text-white" : "bg-white/80 text-gray-600"
                  }`}
                >
                  <Heart size={20} fill={isLiked ? "currentColor" : "none"} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-12 h-12 rounded-full bg-white/80 backdrop-blur-lg flex items-center justify-center text-gray-600"
                >
                  <Share2 size={20} />
                </motion.button>
              </div>

              {/* Discount Badge */}
              {product.discountPercent > 0 && (
                <motion.div
                  initial={{ opacity: 0, rotate: -10 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  className="absolute top-4 left-4 bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-xl font-bold shadow-lg"
                >
                  {product.discountPercent}% OFF
                </motion.div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            <div className="flex gap-3 mt-4">
              {[1, 2, 3, 4].map((item) => (
                <motion.button
                  key={item}
                  whileHover={{ scale: 1.05 }}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${
                    item === 1 ? "border-blue-500" : "border-transparent"
                  }`}
                >
                  <img
                    src={product.imageUrl}
                    alt={`Thumbnail ${item}`}
                    className="w-full h-full object-cover"
                  />
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Product Title */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {product.type}
                </span>
                {product.isCustomizable && (
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium flex items-center gap-1">
                    <Sparkles size={14} />
                    Customizable
                  </span>
                )}
              </div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">{product.name}</h1>
              <p className="text-gray-500">{product.shortDescription}</p>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={18}
                    fill={star <= 4 ? "#fbbf24" : "none"}
                    className={star <= 4 ? "text-yellow-400" : "text-gray-300"}
                  />
                ))}
              </div>
              <span className="text-gray-600">4.8 (124 reviews)</span>
            </div>

            {/* Price */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  {product.discountPercent > 0 || subscription.discount > 0 ? (
                    <div className="space-y-1">
                      <span className="text-3xl font-bold text-gray-400 line-through">
                        ₹{product.price.toLocaleString()}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-4xl font-bold text-green-600">
                          ₹{getFinalPrice(product.price, product.discountPercent).toLocaleString()}
                        </span>
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                          {product.discountPercent || subscription.discount}% OFF
                        </span>
                      </div>
                    </div>
                  ) : (
                    <span className="text-4xl font-bold text-blue-600">
                      ₹{product.price.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Quantity Selector */}
                <div className="flex items-center gap-3">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200"
                  >
                    <Minus size={18} />
                  </motion.button>
                  <span className="text-2xl font-bold text-gray-800 w-12 text-center">
                    {quantity}
                  </span>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200"
                  >
                    <Plus size={18} />
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowModal(true)}
                className="flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-semibold shadow-lg shadow-blue-500/30"
              >
                <ShoppingCart size={20} />
                Buy Now (COD)
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleWalletPayment}
                className="flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl font-semibold shadow-lg shadow-green-500/30"
              >
                <WalletIcon className="w-5 h-5" />
                Pay with Wallet
              </motion.button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Truck, label: "Free Shipping", color: "from-blue-500 to-cyan-500" },
                { icon: Shield, label: "Secure Payment", color: "from-green-500 to-emerald-500" },
                { icon: Clock, label: "24/7 Support", color: "from-purple-500 to-pink-500" },
              ].map((feature) => (
                <motion.div
                  key={feature.label}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-xl p-4 shadow-md border border-gray-100 text-center"
                >
                  <div
                    className={`w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center`}
                  >
                    <feature.icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-xs font-medium text-gray-600">{feature.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Tabs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <div className="flex gap-4 border-b border-gray-200 mb-8">
            {["description", "specifications", "reviews"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 px-4 font-medium capitalize transition-all ${
                  activeTab === tab
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {activeTab === "description" && (
              <motion.div
                key="description"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
              >
                <h3 className="text-xl font-bold text-gray-800 mb-4">Product Description</h3>
                <p className="text-gray-600 leading-relaxed">{product.shortDescription}</p>
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <p className="text-blue-800 font-medium flex items-center gap-2">
                    <Tag size={18} />
                    Features: {product.features}
                  </p>
                </div>
              </motion.div>
            )}

            {activeTab === "specifications" && (
              <motion.div
                key="specifications"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
              >
                <h3 className="text-xl font-bold text-gray-800 mb-6">Technical Specifications</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {specs.map((spec) => (
                    <div
                      key={spec.label}
                      className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                        <spec.icon size={16} />
                        {spec.label}
                      </div>
                      <p className="font-semibold text-gray-800">{spec.value}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === "reviews" && (
              <motion.div
                key="reviews"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
              >
                <h3 className="text-xl font-bold text-gray-800 mb-6">Customer Reviews</h3>
                <div className="space-y-4">
                  {[1, 2, 3].map((review) => (
                    <div key={review} className="p-4 border border-gray-100 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                          {String.fromCharCode(64 + review)}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">Customer {review}</p>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                size={14}
                                fill={star <= 4 ? "#fbbf24" : "none"}
                                className={star <= 4 ? "text-yellow-400" : "text-gray-300"}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm">
                        Great product! Exactly as described and fast shipping. Would recommend to others.
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Modal */}
      {showModal && (
        <CheckoutPopup
          product={product}
          onClose={() => setShowModal(false)}
          onPaymentComplete={handleBuyCOD}
        />
      )}
    </div>
  );
};

// Wallet Icon Component
const WalletIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

export default ProductPage;
